import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Trash2, Users, Building2, Briefcase, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  listWorkspaces,
  deleteWorkspace,
  type Workspace,
} from "@/services/workspacesService";
import { WorkspaceForm } from "./WorkspaceForm";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";

interface WorkspaceManagementProps {
  onBack: () => void;
}

type ViewMode = "list" | "form";

export const WorkspaceManagement = ({ onBack }: WorkspaceManagementProps) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [editingWorkspace, setEditingWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();

  // Carregar dados iniciais
  useEffect(() => {
    loadWorkspaces();
  }, []);

  const loadWorkspaces = async () => {
    setLoading(true);
    try {
      const workspacesRes = await listWorkspaces();

      if (workspacesRes.success && workspacesRes.data?.data) {
        setWorkspaces(Array.isArray(workspacesRes.data.data) ? workspacesRes.data.data : []);
      } else {
        setWorkspaces([]);
        if (workspacesRes.error?.code === 'MISSING_TOKEN' || workspacesRes.error?.code === 'INVALID_TOKEN') {
          toast({
            title: "Erro de Autenticação",
            description: "Você precisa fazer login para acessar esta página",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      setWorkspaces([]);
      toast({
        title: "Erro",
        description: "Erro ao carregar workspaces",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWorkspace = async () => {
    await loadWorkspaces();
    setViewMode("list");
    setEditingWorkspace(null);
  };

  const handleNewWorkspace = () => {
    setEditingWorkspace(null);
    setViewMode("form");
  };

  const handleEdit = (workspace: Workspace) => {
    setEditingWorkspace(workspace);
    setViewMode("form");
  };

  const handleDelete = async (workspace: Workspace) => {
    if (!confirm(`Tem certeza que deseja remover o workspace "${workspace.name}"?`)) {
      return;
    }

    try {
      const result = await deleteWorkspace(workspace.id);

      if (result.success) {
        toast({
          title: "Workspace removido",
          description: `Workspace "${workspace.name}" foi removido com sucesso.`
        });
        await loadWorkspaces();
      } else {
        toast({
          title: "Erro",
          description: result.error?.message || "Erro ao remover workspace",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao remover workspace",
        variant: "destructive"
      });
    }
  };

  if (viewMode === "form") {
    return (
      <WorkspaceForm
        onBack={() => setViewMode("list")}
        onSave={handleSaveWorkspace}
        editingWorkspace={editingWorkspace}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando workspaces...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard
      permission="res_workspaces.read"
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>Você não tem permissão para visualizar workspaces.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      }
    >
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Briefcase}
        title="Gerenciamento de Workspaces"
        description="Configure workspaces para organizar usuários por área, projeto ou equipe"
        onBack={onBack}
        actions={
          canCreate("workspaces") && (
            <Button
              className="bg-gradient-primary text-white hover:opacity-90"
              onClick={handleNewWorkspace}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Workspace
            </Button>
          )
        }
      />

      <main className="container mx-auto px-6 py-6 space-y-6">

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Workspaces Cadastrados
          </CardTitle>
          <CardDescription>
            Lista de todos os workspaces organizados por cliente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workspace</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Especialistas</TableHead>
                <TableHead>Usuários</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workspaces.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Nenhum workspace cadastrado
                  </TableCell>
                </TableRow>
              ) : (
                workspaces.map((workspace) => (
                  <TableRow key={workspace.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{workspace.name || 'Sem nome'}</div>
                        <div className="text-sm text-muted-foreground">
                          {workspace.description || 'Sem descrição'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{workspace.client?.company || workspace.client_company || 'Cliente não informado'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {workspace.products && workspace.products.length > 0 ? (
                          <>
                            {workspace.products.slice(0, 2).map((product) => (
                              <Badge key={product.id} variant="secondary" className="text-xs">
                                {product.name}
                              </Badge>
                            ))}
                            {workspace.products.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{workspace.products.length - 2} mais
                              </Badge>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">Nenhum especialista</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{workspace.user_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={workspace.status?.name === 'active' ? 'default' : 'secondary'}>
                        {workspace.status?.name === 'active' ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {workspace.created_at ? new Date(workspace.created_at).toLocaleDateString('pt-BR') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        {canUpdate("workspaces") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(workspace)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete("workspaces") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(workspace)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </main>
    </div>
    </PermissionGuard>
  );
};
